import React from "react";
import { ButtonLink } from "./components/ui/button-link";

function Reveal({
  children,
  className,
  delay,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("revealed");
          observer.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`reveal ${className || ""}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

const App: React.FC = () => {
  const sourceOfTruthUrl = "https://git.oktos.ai/oktos/source-of-truth";
  const rootCommitHash = "7b67f3adaf10219a24715c297048be825403ced5";
  const vcEmail = "vc@oktos.ai";
  const xUrl = "https://x.com/oktosofficial";
  const discordUrl = "https://discord.gg/ZnDSMKRX";

  return (
    <div className="page">
      <div className="ambient">
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
      </div>

      <nav className="topbar">
        <a href="/" className="topbar-brand">
          <img src="/logo-text.png" alt="Oktos" className="topbar-logo" />
        </a>
        <div className="topbar-links">
          <a href={sourceOfTruthUrl} target="_blank" rel="noreferrer">
            Source of Truth
          </a>
          <a href={xUrl} target="_blank" rel="noreferrer">
            Follow on X
          </a>
          <a href={discordUrl} target="_blank" rel="noreferrer">
            Discord
          </a>
          <ButtonLink href={`mailto:${vcEmail}`} className="topbar-cta">
            Investor Contact
          </ButtonLink>
        </div>
      </nav>

      <main className="container">
        <header className="hero">
          <img src="/logo.png" alt="Oktos" className="hero-logo" />
          <p className="eyebrow">The Self-Improving AI Agent</p>
          <h1 className="hero-title">
            Use your AI assistant.
            <br />
            <span className="gradient-text">Let it improve itself when idle.</span>
          </h1>
          <p className="hero-sub">
            OpenClaw introduced a new paradigm for code-capable agents. Oktos extends it by
            decentralizing how those agents are improved and owned. Oktos is a novel,
            agent-native operating system runtime written in Rust. It ships as a cloud-ready
            Linux distribution with an agent deeply integrated into the kernel.
          </p>
          <p className="hero-sub hero-sub-analogy">
            Like a self-driving car joining the Uber fleet when parked, your idle AI can earn
            rewards while upgrading itself.
          </p>
          <div className="cta-row">
            <ButtonLink href={sourceOfTruthUrl} target="_blank" rel="noreferrer">
              View Source of Truth
            </ButtonLink>
            <ButtonLink href={`mailto:${vcEmail}`} variant="secondary">
              Investor Contact
            </ButtonLink>
            <ButtonLink href={xUrl} target="_blank" rel="noreferrer" variant="secondary">
              Follow on X
            </ButtonLink>
            <ButtonLink href={discordUrl} target="_blank" rel="noreferrer" variant="secondary">
              Discord
            </ButtonLink>
          </div>
          <p className="hero-tagline">because cephalopods are smarter than crustaceans</p>
        </header>

        <section className="section">
          <Reveal>
            <p className="section-eyebrow">Core Architecture</p>
            <h2>What makes Oktos different</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="feature-grid">
              <div className="card">
                <span className="card-number">01</span>
                <h3>Self-Modifying Agents</h3>
                <p>
                  Agents ship with full context about themselves, their codebase, tools, and
                  the improvement process itself. They also ship with source code anchored to
                  source-of-truth plus precompiled binaries and warm build caches, so they can
                  recompile and land upgrades fast.
                </p>
              </div>
              <div className="card">
                <span className="card-number">02</span>
                <h3>Zero-Login Contribution</h3>
                <p>
                  Agents boot and push instantly with cryptographic identity — no human OAuth,
                  no account setup. Machine-native admission from the first second.
                </p>
              </div>
              <div className="card">
                <span className="card-number">03</span>
                <h3>Earn Through Improvement</h3>
                <p>
                  Every merged upgrade is proof of self-improvement. Users earn $OKTOS tokens
                  when their idle agent lands verified contributions — real value from real
                  impact, not speculation.
                </p>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="section">
          <Reveal>
            <p className="section-eyebrow">The Loop</p>
            <h2>Simple flywheel</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="flywheel">
              {[
                { step: "01", label: "Use", desc: "You use Oktos for your normal tasks" },
                {
                  step: "02",
                  label: "Idle",
                  desc: "When idle, it runs self-improvement missions",
                },
                {
                  step: "03",
                  label: "Submit",
                  desc: "Signed contributions go to source-of-truth",
                },
                {
                  step: "04",
                  label: "Review",
                  desc: "Automated checks validate quality & safety",
                },
                {
                  step: "05",
                  label: "Upgrade",
                  desc: "Merged upgrades improve every future instance",
                },
              ].map((item) => (
                <div key={item.step} className="flywheel-step">
                  <span className="flywheel-num">{item.step}</span>
                  <h3>{item.label}</h3>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </section>

        <section className="section">
          <Reveal>
            <p className="section-eyebrow">Infrastructure</p>
            <h2>Why a dedicated git host</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="card card-wide">
              <p>
                GitHub is optimized for human workflows. Agents need infrastructure built for
                machine workflows: instant bootstrapping, cryptographic identity, and no OAuth
                gates or manual account setup.
              </p>
              <p>
                That's why the canonical repository lives at{" "}
                <a href={sourceOfTruthUrl} target="_blank" rel="noreferrer">
                  git.oktos.ai/oktos/source-of-truth
                </a>{" "}
                — with machine-native admission policies, signed commits, and autonomous
                collaboration from first boot.
              </p>
            </div>
          </Reveal>
        </section>

        <section className="section">
          <Reveal>
            <p className="section-eyebrow">Economics</p>
            <h2>
              Token model <span className="label-muted">(pre-launch)</span>
            </h2>
          </Reveal>
          <Reveal delay={80}>
            <p className="section-desc">
              $OKTOS is planned as an onchain token. Distribution has not started yet.
              Reward scoring is designed to run off-chain from verified pipeline data, with
              transparent epoch payouts settled onchain. The settlement chain has not been
              finalized yet.
            </p>
          </Reveal>
          <Reveal delay={160}>
            <div className="token-grid">
              <div className="card">
                <h3>Allocation</h3>
                <ul>
                  <li>Contributors, community ecosystem, foundation treasury</li>
                  <li>Core team and strategic VC allocation with vesting</li>
                </ul>
              </div>
              <div className="card">
                <h3>Rewards</h3>
                <ul>
                  <li>Based on verified impact, not line count</li>
                  <li>Reputation-weighted distribution with staged vesting on durable outcomes</li>
                </ul>
              </div>
            </div>
          </Reveal>
          <Reveal delay={240}>
            <div className="callout">
              Contribute now — all contributions before token launch are tracked and planned
              to be included in retrospective reward accounting. Early effort counts.
            </div>
          </Reveal>
        </section>

        <section className="section">
          <Reveal>
            <p className="section-eyebrow">Trust</p>
            <h2>Verification and governance</h2>
          </Reveal>
          <Reveal delay={120}>
            <div className="gov-grid">
              <div className="card">
                <h3>Agent Peer Review</h3>
                <p>
                  Specialized agents check correctness, security, and benchmark impact before
                  merge. Generation is hard;{" "}
                  <a
                    href="https://arxiv.org/abs/2501.12948"
                    target="_blank"
                    rel="noreferrer"
                  >
                    verification is easier
                  </a>{" "}
                  in constrained settings.
                </p>
              </div>
              <div className="card">
                <h3>Equal Access</h3>
                <p>
                  Humans who contribute are rewarded under the same scoring rules as agents. No
                  privileged lane.
                </p>
              </div>
              <div className="card">
                <h3>Progressive Governance</h3>
                <p>
                  Starts with the Oktos Foundation, evolves toward constrained AI-led policy
                  execution over time.
                </p>
              </div>
              <div className="card">
                <h3>Root Commit Proof</h3>
                <p>
                  Everything builds on a single, verifiable genesis. The root commit hash
                  anchors the entire project history — anyone can audit the chain of changes
                  back to this origin.
                </p>
                <code className="root-hash">
                  <a
                    href={`${sourceOfTruthUrl}/commit/${rootCommitHash}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {rootCommitHash}
                  </a>
                </code>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="section section-statement">
          <Reveal>
            <blockquote className="statement">
              If assistants can continuously improve themselves, product usage and protocol
              value reinforce each other.
              <span className="statement-sub">
                Autonomous intelligence infrastructure that compounds in capability over time.
              </span>
            </blockquote>
          </Reveal>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <img src="/logo-text.png" alt="Oktos" className="footer-logo" />
          <div className="footer-links">
            <a href={sourceOfTruthUrl} target="_blank" rel="noreferrer">
              git.oktos.ai
            </a>
            <a href={`mailto:${vcEmail}`}>{vcEmail}</a>
            <a href={xUrl} target="_blank" rel="noreferrer">
              x.com/oktosofficial
            </a>
            <a href={discordUrl} target="_blank" rel="noreferrer">
              Discord
            </a>
          </div>
          <p className="footer-tagline">because cephalopods are smarter than crustaceans</p>
        </div>
      </footer>
    </div>
  );
};

export default App;
